'use client';

import { toast } from 'react-toastify';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import LinkIcon from '@mui/icons-material/Link';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, Breadcrumbs, IconButton, Link as MuiLink, Typography } from '@mui/material';

import { useT } from '@/hooks';

import { ROUTES } from '@/constants';
import { MESSAGES } from '@/constants/messages.constants';

interface GameDetailHeaderProps {
  sportSlug: string;
  title: string;
  isBookmarked?: boolean;
  onBookmarkClick?: (e?: React.MouseEvent) => void;
}

export const GameDetailHeader = ({
  sportSlug,
  title,
  isBookmarked,
  onBookmarkClick,
}: GameDetailHeaderProps) => {
  const { t } = useT();

  const handleCopyLink = () => {
    if (typeof window !== 'undefined') {
      navigator.clipboard.writeText(window.location.href);
      toast.success(MESSAGES.TOAST.LINK_COPIED_CLIPBOARD);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
          aria-label="breadcrumb"
          sx={{ '& .MuiBreadcrumbs-li': { fontSize: '0.875rem' } }}
        >
          <MuiLink underline="hover" color="text.secondary" href={ROUTES.SPORTS}>
            {t('nav.sports', { defaultValue: 'Sports' })}
          </MuiLink>
          <Typography color="text.secondary" sx={{ fontWeight: 500, textTransform: 'uppercase' }}>
            {sportSlug === 'previous'
              ? t('sports.previous', { defaultValue: 'Previous' })
              : sportSlug}
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={handleCopyLink} sx={{ color: 'text.secondary' }}>
            <LinkIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={onBookmarkClick}
            sx={{ color: isBookmarked ? 'primary.main' : 'text.secondary' }}
          >
            {isBookmarked ? (
              <BookmarkIcon sx={{ fontSize: 20 }} />
            ) : (
              <BookmarkBorderIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Box>
      </Box>

      <Typography
        variant="h3"
        component="h1"
        sx={{
          fontWeight: 700,
          fontSize: { xs: '1.75rem', md: '2.25rem' },
          color: 'text.primary',
          textAlign: { xs: 'center', md: 'left' },
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};
